import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Building2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { isDemo } from '../account/beta.ts';
import { useAccount } from '../account/store.ts';
import { apiMessage } from '../api/errors.ts';
import { api } from '../api/instance.ts';
import { checkoutTeam } from '../plans/billing.ts';
import { formatAmount } from '../plans/features.ts';
import { priceText } from '../plans/PlanCard.tsx';
import { loadPlans, usePlans } from '../plans/store.ts';
import { services } from '../services/index.ts';
import { toast } from '../state/feedback.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { Segmented } from '../ui/Segmented.tsx';

/** Create a company workspace, then Team checkout with seats. */
export function TeamNewScreen() {
  const navigate = useNavigate();
  const session = useAccount((a) => a.session);
  const ready = useAccount((a) => a.ready);
  const catalog = usePlans((p) => p.catalog);
  const [name, setName] = useState('');
  const [seats, setSeats] = useState('3');
  const [interval, setBilling] = useState<'month' | 'year'>('year');
  const [busy, setBusy] = useState(false);
  const team = catalog?.plans.find((p) => p.seats.perSeat) ?? null;
  const n = Math.max(team?.seats.min ?? 2, Number.parseInt(seats, 10) || 0);
  const unit = team?.prices.find((p) => p.interval === interval)?.amount;

  useEffect(() => {
    if (ready && !session) void navigate({ to: '/signin', search: { next: '/team/new' }, replace: true });
    void loadPlans();
  }, [ready, session, navigate]);

  return (
    <main
      className="min-h-dvh"
      style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'calc(var(--safe-bottom) + 32px)' }}
    >
      <div className="flex min-h-11 items-center gap-2 px-2">
        <IconButton
          icon={ArrowLeft}
          label="Back"
          onClick={() => (history.length > 1 ? history.back() : void navigate({ to: '/account' }))}
        />
        <h1 className="t-title">New company</h1>
      </div>
      <form
        className="mx-auto flex max-w-[480px] flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            const org = await api.createOrg(name.trim());
            await services.account.refreshMe();
            services.account.setActiveScope(org.id);
            // Demo accounts can create a company but never reach checkout.
            const ok = isDemo(useAccount.getState().me) ? false : await checkoutTeam(org.id, n, interval);
            if (!ok) await navigate({ to: '/team/$orgId', params: { orgId: org.id } });
          } catch (err) {
            toast(apiMessage(err, 'Could not create the company.'), 'error');
          } finally {
            setBusy(false);
          }
        }}
      >
        <Building2 className="text-accent" size={28} aria-hidden />
        <p className="t-body text-dim">
          One price list, company branding on every quote, shared projects and monthly AI credits for each rep.
        </p>
        <Field
          label="Company name"
          required
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Bright Eaves LLC"
        />
        <div className="flex items-end gap-3">
          <Field
            label="Seats"
            className="w-28"
            inputMode="numeric"
            value={seats}
            onChange={(e) => setSeats(e.target.value.replace(/\D/g, ''))}
          />
          <Segmented
            label="Billing interval"
            size="sm"
            className="flex-1"
            value={interval}
            onChange={setBilling}
            options={[
              { value: 'month', label: 'Monthly' },
              { value: 'year', label: 'Yearly' },
            ]}
          />
        </div>
        {team && unit != null ? (
          <p className="t-label tabular" data-testid="team-total">
            {priceText(team, interval, catalog)} × {n} = {formatAmount(unit * n, catalog?.currency)}
            {interval === 'month' ? '/mo' : '/yr'}
          </p>
        ) : null}
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!name.trim()}>
          Continue to checkout
        </Button>
      </form>
    </main>
  );
}
